import DOMPurify from 'dompurify';
import { setParams, getParams } from './params.js';
import syncListboxes from './sync-listboxes.js';

/**
 * Display Pills of active filters in filters footer.
 * @param  {object}   block                the block.
 * @param  {object}   inputs               all of the filter inputs.
 * @param  {Function} getFilterableContent Fetch the content.
 */
export const setActiveFilters = (block, inputs, getFilterableContent) => {
	const contentContainer = block.querySelector('.wp-block-embark-filterable-content__listings');
	const filterFooter = block.querySelector('.wp-block-embark-filterable-content__footer');
	const activeContainer = block.querySelector('.wp-block-embark-filterable-content__active-filters');
	const checked = {};

	// Loop through the inputs and get the set values.
	inputs.forEach((input) => {
		const { dataset, value } = input;
		const { tax } = dataset;

		// If is checked set the tax parameters.
		if (input.checked && tax) {
			if (checked[tax] !== undefined) {
				checked[tax].push(value);
			} else {
				checked[tax] = [value];
			}
		}

		// If is search set the search parameter.
		if (input.classList.contains('filter-search') && value) {
			checked.search = DOMPurify.sanitize(value); // Sanitize the search value
		}
	});

	// Check if "checked" object has values.
	if (Object.keys(checked).length > 0) {
		// Show filter footer.
		filterFooter.style.display = 'flex';

		// Output the pills!
		let html = '';
		Object.keys(checked).forEach((k) => {
			const values = checked[k];

			if (k === 'search') {
				const input = block.querySelector('[name="search"]');
				// Sanitize the search input value before inserting it into the HTML
				html += `<button tabindex="0" data-filter="search">${DOMPurify.sanitize(input.value)}</button>`;
			} else {
				values.forEach((value) => {
					const input = block.querySelector(`[name="${k}_${value}"]`);
					if (input) {
						// Sanitize input data before generating the button HTML
						html += `<button tabindex="0" data-filter="${DOMPurify.sanitize(`${k}_${value}`)}">${DOMPurify.sanitize(input.dataset.name)}</button>`;
					}
				});
			}
		});

		// Safely insert the generated HTML into the DOM
		activeContainer.innerHTML = DOMPurify.sanitize(html);

		// Get all the pills and assign onclick event handlers.
		const activeFilters = activeContainer.querySelectorAll('button');
		activeFilters.forEach((pill) => {
			const handlePill = (e) => {
				// Sanitize the data attribute before using it
				const input = block.querySelector(`[name="${DOMPurify.sanitize(e.target.dataset.filter)}"]`);

				if (input.type === 'checkbox') {
					input.checked = false;
				} else {
					input.value = '';
				}

				setParams(block);
				syncListboxes();

				// Reset the active filters.
				setActiveFilters(block, inputs);

				// Fetch the content.
				getFilterableContent(contentContainer, getParams(block));
			};

			pill.addEventListener('click', (e) => handlePill(e));
			pill.addEventListener('keyup', (e) => {
				e.preventDefault();
				if (e.code === 'Enter' || e.code === 'Space') {
					handlePill(e);
				}
			});
		});
	} else {
		// If "checked" object empty (no filters checked) hide the filter footer.
		filterFooter.style.display = 'none';
	}
};

/**
 * Drop every filter and the search term, then refetch.
 *
 * Shared by the Clear Results button and the "All …" option at the top of each
 * dropdown, which resets the listing rather than just its own field.
 * @param  {object}   block                the block.
 * @param  {object}   inputs               all of the filter inputs.
 * @param  {Function} getFilterableContent Fetch the content.
 */
export const clearAllFilters = (block, inputs, getFilterableContent) => {
	const contentContainer = block.querySelector('.wp-block-embark-filterable-content__listings');
	const filterContainer = block.querySelector('.wp-block-embark-filterable-content__filter-container');
	const filterFooter = block.querySelector('.wp-block-embark-filterable-content__footer');

	// Hide the filter footer.
	filterFooter.style.display = 'none';
	inputs.forEach((e) => {
		if (e.checked) {
			e.checked = false;
		}

		// If is search set the search parameter.
		if (e.classList.contains('filter-search')) {
			e.value = '';
		}
	});

	// Back to the first page, or an unfiltered listing would open partway down.
	filterContainer.dataset.paged = 1;

	setParams(block);
	syncListboxes();

	// Fetch the content.
	getFilterableContent(contentContainer, getParams(block));
};

/**
 * Initiate the Clear Results button.
 * @param  {object}   block                the block.
 * @param  {object}   inputs               all of the filter inputs.
 * @param  {Function} getFilterableContent Fetch the content.
 */
export const clearFilters = (block, inputs, getFilterableContent) => {
	const clear = block.querySelector('.wp-block-embark-filterable-content__clear-filters button');

	clear.addEventListener('click', () => {
		clearAllFilters(block, inputs, getFilterableContent);
	});
};
